import {
  PolicyInputValidState,
  PreloaderButtonState,
  PreloaderSubmitButton,
  Requirement,
  SimplePasswordInput
} from "@monorepo/components";
import { AuthFlowBuilder } from "@monorepo/libraries";
import { StandardForm } from "@vp/swan";
import { Auth0Error, Captcha } from "auth0-js";
import React, {
  FormEvent,
  useContext,
  useEffect,
  useState,
  useRef
} from "react";
import { useTranslation } from "../../useTranslation";
import { SignAuth0Context } from "../../Auth0Context";
import {
  useNotificationContext,
  Notification as NotificationContext
} from "../../NotificationContext";
import { useAuthenticationContext } from "../AuthenticationContext";
import EmailInput from "../Components/EmailInput";
import hide from "../hide.svg";
import show from "../show.svg";

import { pushDataToSegment, saveLastAttemptInfo } from "../../segmentData";
import { SIGN_IN_ATTEMPT, SIGN_IN_PAGE } from "../../constant";
import { Wauth } from "@monorepo/components/lib/hooks/useWauth";
import { Screens } from "../ScreensLayout";

interface SignInProps {
  notificationRef?: React.RefObject<HTMLDivElement>;
  renderResetScreen: Function;
  setShowSocialSignIn: Function;
  setShowLoginWithEmail: Function;
  onChangeScreen: Function;
  wauth: Wauth;
  renderTermsAndConditions: Function;
}

const SignInForm = (props: SignInProps) => {
  const {
    notificationRef,
    renderResetScreen,
    setShowSocialSignIn,
    setShowLoginWithEmail,
    wauth,
    onChangeScreen,
    renderTermsAndConditions
  } = props;
  const [t] = useTranslation("signIn");

  const {
    contextEmail,
    canShowCallbackError,
    setContextEmail,
    validateEmail,
    disableVisualizationOfCallbackError
  } = useAuthenticationContext();
  const {
    loginFailed,
    invalidFormSubmitted,
    callbackErrorLoginFailed,
    notification
  } = useNotificationContext();
  const auth0 = useContext(SignAuth0Context);
  const [email, setEmail] = useState({
    value: contextEmail || wauth?.enforcedEmail || "",
    isValid: true
  });
  const [password, setPassword] = useState({ value: "", isValid: false });
  const [submitState, setSubmitState] = useState(PreloaderButtonState.Disabled);
  const [emailAutofilled, setEmailAutofilled] = useState(false);
  const [passwordAutofilled, setPasswordAutofilled] = useState(false);
  const [captcha, setCaptcha] = useState<Captcha>();
  const myRef = useRef(null);

  useEffect(() => {
    if (myRef.current && auth0.renderCaptcha) {
      setCaptcha(
        auth0.renderCaptcha(document.querySelector(".captcha-container") as HTMLElement, {
          lang: wauth?.culture
        })
      );
    }
  }, [auth0, wauth]);

  useEffect(() => {
    if (canShowCallbackError) {
      let authParams = new AuthFlowBuilder().build();

      if (authParams.loginFailed) {
        callbackErrorLoginFailed(authParams.loginFailed, {
          email: wauth?.enforcedEmail
        });
      }
      if (
        notification?.notification ===
        NotificationContext.LoginFailedCallbackError
      ) {
        disableVisualizationOfCallbackError();
      }
    }
  }, [
    canShowCallbackError,
    disableVisualizationOfCallbackError,
    callbackErrorLoginFailed,
    notification,
    wauth
  ]);

  useEffect(() => {
    if (validateEmail(contextEmail)) {
      setEmail({ value: contextEmail, isValid: true });
    }
  }, [contextEmail, validateEmail]);

  useEffect(() => {
    if (email.value !== "" || password.value !== "") {
      setShowSocialSignIn(false);
      setShowLoginWithEmail(false);
    }
  }, [email, password, setShowSocialSignIn, setShowLoginWithEmail]);

  const handlePasswordChange = (newValue: string, isValid: boolean) => {
    setPasswordAutofilled(false);

    setPassword({ value: newValue, isValid });
  };

  const onEmailChanged = (email: string, isValid: boolean): void => {
    setEmailAutofilled(false);
    if (isValid) {
      setContextEmail(email);
    }

    setEmail({ value: email, isValid });
  };

  const handlePasswordErrorMessages = (validity: string): string => {
    if (validity !== PolicyInputValidState) {
      return t("app:password.missing");
    }

    return "";
  };

  const switchToResetPassword = () => {
    onChangeScreen(Screens.Reset);
  };

  const loginCallback = (err: Auth0Error | null): void => {
    if (err) {
      if (err.code) {
        loginFailed(
          err.code,
          wauth.isFirstLogin,
          switchToResetPassword,
          email.value
        );
      } else {
        loginFailed();
      }

      captcha?.reload(() => {
        console.log("captcha reloaded");
      });

      if (notificationRef && notificationRef.current) {
        notificationRef.current.scrollIntoView({ behavior: "smooth" });
      }
    }

    setSubmitState(PreloaderButtonState.Enabled);
  };

  const onSignInSubmit = (e: FormEvent): void => {
    e.preventDefault();
    saveLastAttemptInfo({ label: "Auth0" });
    pushDataToSegment(SIGN_IN_ATTEMPT, false, {
      label: "Auth0",
      pageName: SIGN_IN_PAGE
    });

    if (submitState !== PreloaderButtonState.Enabled) {
      invalidFormSubmitted(SIGN_IN_PAGE);
    } else {
      setSubmitState(PreloaderButtonState.Preloader);
      auth0.login(
        email.value,
        password.value,
        loginCallback,
        captcha?.getValue()
      );
    }
  };

  const onEmailAutofill = () => {
    setEmailAutofilled(true);
  };

  const onPasswordAutofill = () => {
    setPasswordAutofilled(true);
  };

  useEffect(() => {
    if (email.isValid && password.isValid) {
      setSubmitState(PreloaderButtonState.Enabled);
    } else if (emailAutofilled && passwordAutofilled) {
      setSubmitState(PreloaderButtonState.Enabled);
    } else {
      setSubmitState(PreloaderButtonState.Disabled);
    }
  }, [email.isValid, password.isValid, emailAutofilled, passwordAutofilled]);

  return (
    <>
      <StandardForm
        id="signin-form"
        skin="tight"
        method="post"
        onSubmit={onSignInSubmit}
        noValidate
      >
        <EmailInput
          disabled={!!((wauth.enforcedEmail && wauth.isSteppedUp) || (wauth.enforcedEmail && wauth.disableEmailField))}
          id="sign-in-email"
          email={email.value}
          validateEmail={validateEmail}
          onEmailChanged={onEmailChanged}
          required={Requirement.Required}
          onAutofill={onEmailAutofill}
          autoComplete={"Email"}
        />
        <SimplePasswordInput
          id="signInPassword"
          placeholder={t("app:password.placeholder")}
          value={password.value}
          onPasswordChanged={handlePasswordChange}
          policyErrorMessage={handlePasswordErrorMessages}
          required={Requirement.Required}
          onAutofill={onPasswordAutofill}
          autoComplete={"current-password"}
          passwordVisibilityIcons={{
            show: {
              content: show,
              alt: t("app:password.show"),
              title: t("app:password.show")
            },
            hide: {
              content: hide,
              alt: t("app:password.hide"),
              title: t("app:password.hide")
            }
          }}
        />
        {renderResetScreen()}
        {renderTermsAndConditions()}
        <div
          className="captcha-container"
          ref={myRef}
          style={{ marginBottom: "10px", textAlign: "center" }}
        ></div>
        <PreloaderSubmitButton
          name="submit"
          text={t("submit.text")}
          preloaderText={t("submit.preloaderText")}
          preloaderAccessibilityLabel={t("submit.preloaderAccessibilityLabel")}
          state={submitState}
        />
      </StandardForm>
    </>
  );
};

export default SignInForm;
